import { isAuthenticated } from '~/auth/slices/auth.slice'
import { FAMILY_LICENSE_STATUS, FAMILY_ROLE, FamilyData, FamilyInfo } from '~/familyAccount/types/familyAccounts.types'
import useCurrentUser from '~/hooks/useCurrentUser'
import { useGetFamilyInfoQuery, useGetFamilyQuery } from '~/redux/apis/family-api'
import { useAppSelector } from '~/redux/hooks'

export default function useCurrentFamily(): {
  familyUserData?: FamilyData[]
  currentUserData?: FamilyData
  familyInfo?: FamilyInfo
  availableLicensesCount: number
  isFamilyMember: boolean
  isFamilyAdmin: boolean
  hasLicense: boolean
  isAdminPopupDismissed: boolean
  hasValidSubscription: boolean
  isFamilyLoading: boolean
  isFamilyQuerySuccess: boolean
  isFamilyQueryError: boolean
  isFamilySettled: boolean
  hasFamily: boolean
  refetchFamily: () => void
} {
  const auth = useAppSelector(isAuthenticated)
  const user = useCurrentUser()
  const {
    data: familyData,
    isLoading: isFamilyLoading,
    isSuccess: isFamilyQuerySuccess,
    isError: isFamilyQueryError,
    refetch: refetchFamily,
  } = useGetFamilyQuery(undefined, { skip: !auth })
  const familyUserData = familyData?.familyProfiles || []
  const {
    data: familyInfo,
    isSuccess: isFamilyInfoSuccess,
    isError: isFamilyInfoError,
  } = useGetFamilyInfoQuery(undefined, { skip: !auth })
  const currentUserData = familyUserData?.find((member) => member.userId === user?.id)

  // Both queries have answered, so an empty family is a real answer and not a pending one
  const isFamilySettled = (isFamilyQuerySuccess || isFamilyQueryError) && (isFamilyInfoSuccess || isFamilyInfoError)
  // Derived from the data, not isSuccess: a rejected refetch keeps the profiles but clears the flag,
  // which would strand a real family admin — the `family` tag is invalidated often enough to hit it
  const hasFamily = familyUserData.length > 0
  const hasLicense = Boolean(
    currentUserData?.licenseStatus === FAMILY_LICENSE_STATUS.ACTIVE ||
      currentUserData?.licenseStatus === FAMILY_LICENSE_STATUS.PENDING_REMOVAL,
  )

  // removalInfo can be absent — without the fallback the whole count goes NaN
  const availableLicensesCount =
    typeof familyInfo?.activeMemberCount === 'number' && typeof familyInfo?.licenseCount === 'number'
      ? familyInfo.licenseCount - familyInfo.activeMemberCount - (familyInfo.removalInfo?.withUserPendingRemoval ?? 0)
      : 0

  const isFamilyAdmin =
    familyUserData?.some((member) => member.userId === user?.id && member.role === FAMILY_ROLE.ADMIN) || false
  const isFamilyMember = !isFamilyAdmin && Boolean(familyInfo)

  return {
    familyUserData,
    familyInfo,
    availableLicensesCount,
    isFamilyMember,
    isFamilyAdmin,
    currentUserData,
    hasLicense,
    hasValidSubscription: Boolean(familyData?.hasValidSubscription),
    isAdminPopupDismissed: familyData?.isAdminPopupDismissed ?? false,
    isFamilyLoading,
    isFamilyQuerySuccess,
    isFamilyQueryError,
    isFamilySettled,
    hasFamily,
    refetchFamily,
  }
}
